import axios from "axios";
import { handleException } from "./http";
import {
  describeFailure,
  isCanceledError,
  isConnectionReportedSlow,
  isOffline,
  isTimeoutError,
} from "./requestErrors";

/**
 * POST for sign-in style endpoints, built for weak or unstable connections.
 *
 *  - Offline pre-check: fails at once, without a request, when the device
 *    reports no connection.
 *  - Adaptive timeout: each attempt waits longer than the last, and starts
 *    longer still when the browser reports a 2G-class link.
 *  - Exponential backoff with jitter between attempts.
 *  - Retries only failures a retry can fix: no response, timeout, or a
 *    gateway error (502/503/504). A wrong password is never retried.
 *  - Cancellable through `signal`.
 *
 * Retrying sign-in is safe: the API issues a fresh token pair per call, and a
 * repeat call after a confirmation code was sent answers "pending
 * confirmation code", which the login page already handles.
 */

const { REACT_APP_API_BASE_URL } = process.env;

// Its own instance: the app-wide interceptor rewrites errors into plain
// messages, but here we need the raw status and timing to classify them.
const authClient = axios.create({
  baseURL: REACT_APP_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

const NORMAL_TIMEOUTS_MS = [25000, 40000, 60000];
const SLOW_LINK_TIMEOUTS_MS = [45000, 60000, 75000];
const RETRYABLE_STATUS = new Set([502, 503, 504]);
// After this long without an answer, tell the user the connection looks slow.
export const SLOW_HINT_AFTER_MS = 8000;

const shouldRetry = (error) => {
  if (isCanceledError(error) || isOffline()) return false;
  if (!error?.response) return true;
  return RETRYABLE_STATUS.has(error.response.status);
};

const backoffMs = (attempt) =>
  Math.min(1500 * 2 ** (attempt - 1), 8000) + Math.random() * 500;

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new axios.CanceledError());
    const timer = setTimeout(() => {
      signal?.removeEventListener?.("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new axios.CanceledError());
    };
    signal?.addEventListener?.("abort", onAbort, { once: true });
  });

/**
 * Resolves with the response body (2xx). Rejects with an Error carrying
 * `{ kind, title, message, retryable, error }` from describeFailure, where
 * `error.response.data` is the backend body when there was one.
 *
 * `onProgress({ attempt, maxAttempts, slow, waitingToRetry })` reports what is
 * happening so the page can show it.
 */
export const resilientPost = async (
  path,
  body,
  { signal, onProgress } = {}
) => {
  const report = (status) => onProgress && onProgress(status);
  // Mutable flags in one object so the per-attempt timer can update them.
  const link = { slow: isConnectionReportedSlow(), timedOut: false };

  const fail = async (error) => {
    const described = await describeFailure(error, {
      timedOut: link.timedOut,
    });
    throw Object.assign(new Error(described.message), described, { error });
  };

  if (isOffline()) return fail(new Error("offline"));

  const timeouts = link.slow ? SLOW_LINK_TIMEOUTS_MS : NORMAL_TIMEOUTS_MS;
  const maxAttempts = timeouts.length;

  const attemptOnce = (attempt) => {
    report({ attempt, maxAttempts, slow: link.slow, waitingToRetry: false });
    const slowTimer = setTimeout(() => {
      link.slow = true;
      report({ attempt, maxAttempts, slow: true, waitingToRetry: false });
    }, SLOW_HINT_AFTER_MS);
    return authClient
      .post(path, body, { timeout: timeouts[attempt - 1], signal })
      .finally(() => clearTimeout(slowTimer));
  };

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    let lastError;
    try {
      const response = await attemptOnce(attempt);
      return response.data;
    } catch (error) {
      lastError = error;
    }

    if (isTimeoutError(lastError)) {
      link.timedOut = true;
      link.slow = true;
    }
    if (attempt === maxAttempts || !shouldRetry(lastError)) {
      if (lastError?.response?.status === 400) {
        handleException(lastError.response.data);
      }
      return fail(lastError);
    }

    report({ attempt, maxAttempts, slow: link.slow, waitingToRetry: true });
    try {
      await wait(backoffMs(attempt), signal);
    } catch (canceled) {
      return fail(canceled);
    }
    if (isOffline()) return fail(lastError);
  }
  return undefined;
};
